import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import QueryParams from 'utils/queryParams';
import { useEffect } from 'react';
import { ActionTypes } from 'infrastructure/enums/action-types';

import { useActionTimeManagementFilters } from './use-filter';
import { showResult } from '../utils';

import type { Filters, ActionTimeManagementData } from './types';

const endpoint = 'core-2/admin-tools/action-time-management';
export const getQueryKey = ['actionTimeManagement'] as const;

export const useActionTimeManagement = () => {
  const { filter } = useActionTimeManagementFilters();

  const queryKey = [...getQueryKey, filter] as const;

  const updatedFilter: Filters = {
    ...filter,
    actionTypes: addAdditionalActionTypes(filter.actionTypes),
  };

  const { data, error, isLoading, isPreviousData, isRefetching } = useQuery<
    ActionTimeManagementData | undefined,
    string | undefined
  >({
    queryKey,
    keepPreviousData: true,
    queryFn: () => getItems(updatedFilter),
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return {
    data: {
      data: data?.items,
      count: data?.count,
    },
    isLoading: isLoading || isPreviousData || isRefetching,
    error,
  };
};

const getItems = async (filters: Filters) => {
  const url = QueryParams.stringifyUrl(endpoint, filters);
  const { error, data } = await dataService.getList<ActionTimeManagementData>(
    url,
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

const addAdditionalActionTypes = (actionTypes?: ActionTypes[]) => {
  if (!actionTypes?.length) return actionTypes;

  const types = structuredClone(actionTypes);

  if (actionTypes.includes(ActionTypes.PatientAllergyLog))
    types.push(ActionTypes.PatientAllergyLogUpdate);

  if (actionTypes.includes(ActionTypes.PatientPastMedicalHistory))
    types.push(ActionTypes.PatientPastMedicalHistoryUpdate);

  if (actionTypes.includes(ActionTypes.PatientMedications))
    types.push(
      ActionTypes.PatientMedicationsUpdate,
      ActionTypes.PatientMedicationsDelete,
    );

  if (actionTypes.includes(ActionTypes.PatientICD))
    types.push(ActionTypes.PatientICDUpdate, ActionTypes.PatientICDDelete);

  if (actionTypes.includes(ActionTypes.PatientComment))
    types.push(ActionTypes.CCMPatientCallNotes); // add ccm general comments

  if (actionTypes.includes(ActionTypes.ClinicalNotes))
    types.push(ActionTypes.CCMPatientNotes); // add ccm clinical notes

  return types;
};
